import dayjs from 'dayjs'
import dayjsUtcPlugin from 'dayjs/plugin/utc.js'

import { Month } from './month.js'

const MS_PER_DAY = 86400000

/**
 * Represents the abstract notion of a day without a specific time of day.
 * Does not correspond to a specific physical time range due to the lack of a timezone.
 */
export class Day {
    /**
     * 0 = 1970-01-01
     * 1 = 1970-01-02
     * etc
     */
    private readonly unixDay: number

    /** Derivable from unixDay, but we store both for convenience */
    private readonly ymdStr: string

    private constructor(unixDay: number) {
        if (!Number.isInteger(unixDay)) {
            throw new Error('Non-integer unix day: ' + unixDay)
        }

        this.unixDay = unixDay
        this.ymdStr = new Date(unixDay * MS_PER_DAY).toISOString().substring(0, 10)
    }

    private static fromUtcMidnightTimestamp(timestamp: number): Day {
        return new Day(timestamp / MS_PER_DAY)
    }

    /** @param ymd A string in YYYY-MM-DD format */
    public static fromYmd(ymd: string): Day {
        const ms = new Date(ymd + 'T00:00:00Z').getTime()
        const day = Day.fromUtcMidnightTimestamp(ms)

        if (day.ymdStr !== ymd) {
            throw new Error('Invalid date: ' + ymd)
        }

        return day
    }

    /** The month parameter is 1-based: 1 = January, ..., 12 = December */
    public static fromNumeric(year: number, month: number, dayOfMonth: number): Day {
        const ms = Date.UTC(year, month - 1, dayOfMonth)
        const day = Day.fromUtcMidnightTimestamp(ms)

        if (day.dayOfMonth() !== dayOfMonth || day.month() !== month || day.year() !== year) {
            throw new Error('Invalid date: ' + year + '-' + month + '-' + dayOfMonth)
        }

        return day
    }

    public static fromUtcMidnightDate(date: Date): Day {
        return Day.fromUtcMidnightTimestamp(date.getTime())
    }

    /**
     * @param date
     *   A physical point in time which may correspond to different dates in different time zones.
     * @param timeZone
     *   e.g. 'UTC', 'CET', 'Europe/Helsinki'...
     *   See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat#timezone
     */
    public static fromDate(date: Date, timeZone: string): Day {
        const ymd = Day.dateToYmd(date, timeZone)
        return Day.fromYmd(ymd)
    }

    /** @param timeZone See Day.fromDate() */
    public static today(timeZone: string): Day {
        return Day.fromDate(new Date(), timeZone)
    }

    /** @param timeZone See Day.fromDate() */
    private static dateToYmd(date: Date, timeZone: string): string {
        // The Intl API does not give us a simple way to get the timezone offset,
        // it mainly just uses timezone data for formatting dates to strings.
        // It also doesn't have a simple way of reliably using the YYYY-MM-DD format,
        // so we need to use formatToParts and assemble them on our own.

        const namedParts = { year: '', month: '', day: '' }
        const parts = new Intl.DateTimeFormat('en', { timeZone }).formatToParts(date)

        for (const part of parts) {
            if (part.type === 'year' || part.type === 'month' || part.type === 'day') {
                namedParts[part.type] = part.value
            }
        }

        const pad = (s: string) => s.padStart(2, '0')

        return `${namedParts.year}-${pad(namedParts.month)}-${pad(namedParts.day)}`
    }

    /**
     * TODO return Month[]?
     * Splits the given date range into an array of months, each represented by a start and end date.
     * @param startDate The start date of the range.
     * @param endDate The end date of the range.
     * @returns An array of objects representing each month, with a start and end date.
     * @throws An error if the start date is after the end date.
     */
    public static splitIntoMonths(startDate: Day, endDate: Day): Array<{ start: Day; end: Day }> {
        if (startDate.isAfter(endDate)) {
            throw new Error('startDate must be <= endDate')
        }

        const months: Array<{ start: Day; end: Day }> = []

        let currentMonthStart = startDate

        while (currentMonthStart.isBefore(endDate)) {
            const currentMonthEnd = currentMonthStart.lastOfMonth()
            const end = Day.min(currentMonthEnd, endDate)
            months.push({ start: currentMonthStart, end })
            currentMonthStart = currentMonthEnd.addDays(1)
        }

        return months
    }

    public static min(day1: Day, day2: Day): Day {
        return day1.unixDay < day2.unixDay ? day1 : day2
    }

    public static max(day1: Day, day2: Day): Day {
        return day1.unixDay > day2.unixDay ? day1 : day2
    }

    public static countDays(day1: Day, day2: Day): number {
        if (day1.unixDay > day2.unixDay) {
            throw new Error(`day1 (${day1.toYmdString()}) must be <= day2 (${day2.toYmdString()})`)
        }

        return day2.unixDay - day1.unixDay + 1
    }

    public static countDaysSafe(day1: Day, day2: Day): number {
        if (day1.unixDay > day2.unixDay) {
            return (day1.unixDay - day2.unixDay) * -1
        }

        return day2.unixDay - day1.unixDay + 1
    }

    /**
     * Number of milliseconds (not seconds) between the Unix epoch (1970-01-01 00:00:00 UTC)
     * and 00:00:00 UTC on this day.
     */
    private toUtcMidnightTimestamp(): number {
        return this.unixDay * MS_PER_DAY
    }

    /**
     * Returns a Date object representing the physical point in time for 00:00:00 UTC on this day
     */
    private toUtcMidnightDate(): Date {
        return new Date(this.toUtcMidnightTimestamp())
    }

    public year(): number {
        return this.toUtcMidnightDate().getUTCFullYear()
    }

    /** 1-based: 1 = January, ..., 12 = December */
    public month(): number {
        return this.toUtcMidnightDate().getUTCMonth() + 1
    }

    /** 0-based: 0 = January, ..., 11 = December */
    public getMonth(): number {
        return this.toUtcMidnightDate().getUTCMonth()
    }

    public dayOfMonth(): number {
        return this.toUtcMidnightDate().getUTCDate()
    }

    public toYmdString(): string {
        return this.ymdStr // YYYY-MM-DD
    }

    public formatWithDayJs(format: string): string {
        if (!dayjs.utc) {
            dayjs.extend(dayjsUtcPlugin)
        }

        return dayjs.utc(this.toUtcMidnightDate()).format(format)
    }

    public toMonth(): Month {
        return new Month(this.year(), this.month())
    }

    /** Used by JSON.stringify() */
    public toJSON(): string {
        return this.ymdStr // YYYY-MM-DD
    }

    /** The pg library checks for the existence of this method when serializing values */
    public toPostgres(): string {
        return this.ymdStr // YYYY-MM-DD
    }

    public addDays(days: number): Day {
        if (!Number.isInteger(days)) {
            throw new Error('Non-integer number of days: ' + days)
        }

        return new Day(this.unixDay + days)
    }

    /**
     * May behave unexpectedly when used on the 29th, 30th, or 31st of a month.
     * For example, what is January 31 + one month? February 28/29 or March 1/2/3?
     * Recommended to use day.firstOfMonth().addMonths(...), then the result is unambiguous.
     */
    public addMonths(months: number): Day {
        if (!Number.isInteger(months)) {
            throw new Error('Non-integer number of months: ' + months)
        }

        const date = this.toUtcMidnightDate()
        date.setUTCMonth(date.getUTCMonth() + months)
        return Day.fromUtcMidnightDate(date)
    }

    public withDayOfMonth(newDayOfMonth: number): Day {
        const currentDayOfMonth = this.dayOfMonth()

        if (newDayOfMonth === currentDayOfMonth) {
            return this
        }

        const newDay = this.addDays(newDayOfMonth - currentDayOfMonth)

        if (newDay.dayOfMonth() === newDayOfMonth) {
            return newDay
        } else {
            // Can happen if the parameter is less than 1, more than 31, etc
            throw new Error('Invalid day of month')
        }
    }

    public firstOfMonth(): Day {
        return this.withDayOfMonth(1)
    }

    public firstOfNextMonth(): Day {
        // Adding 31 days to the first of month will give us a day between the 1st and the
        // 4th of the next month.
        return this.firstOfMonth().addDays(31).firstOfMonth()
    }

    public lastOfMonth(): Day {
        return this.firstOfNextMonth().addDays(-1)
    }

    public isSame(other: Day): boolean {
        return this.unixDay === other.unixDay
    }

    public isBefore(other: Day): boolean {
        return this.unixDay < other.unixDay
    }

    public isBetween(start: Day, end: Day): boolean {
        return this.unixDay >= start.unixDay && this.unixDay <= end.unixDay
    }

    public isAfter(other: Day): boolean {
        return this.unixDay > other.unixDay
    }

    public isSameOrBefore(other: Day): boolean {
        return this.unixDay <= other.unixDay
    }

    public isSameOrAfter(other: Day): boolean {
        return this.unixDay >= other.unixDay
    }
}
